"use client"

import { useState, useEffect } from "react"
import { API_BASE_URL } from "../../config"

const ModifierModal = ({ modifier, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: "",
    selectionType: "MULTIPLE",
    options: [{ name: "", price: 0 }],
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (modifier) {
      const modifierData = modifier.modifier_list_data || {}
      setFormData({
        name: modifierData.name || "",
        selectionType: modifierData.selection_type || "MULTIPLE",
        options: modifierData.modifiers?.map((mod) => ({
          id: mod.id,
          name: mod.modifier_data?.name || "",
          price: mod.modifier_data?.price_money?.amount ? mod.modifier_data.price_money.amount / 100 : 0,
        })) || [{ name: "", price: 0 }],
      })
    }
  }, [modifier])

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, { name: "", price: 0 }],
    })
  }

  const removeOption = (index) => {
    if (formData.options.length > 1) {
      const newOptions = formData.options.filter((_, i) => i !== index)
      setFormData({ ...formData, options: newOptions })
    }
  }

  const updateOption = (index, field, value) => {
    const newOptions = [...formData.options]
    newOptions[index] = { ...newOptions[index], [field]: value }
    setFormData({ ...formData, options: newOptions })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const url = modifier
        ? `${API_BASE_URL}/api/admin/modifiers/${modifier.id}`
        : `${API_BASE_URL}/api/admin/modifiers`

      const method = modifier ? "PUT" : "POST"

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify({
          name: formData.name,
          selectionType: formData.selectionType,
          options: formData.options,
        }),
      })

      if (response.ok) {
        onSave()
      } else {
        const error = await response.json()
        alert(error.error || "Failed to save modifier")
      }
    } catch (error) {
      console.error("Error saving modifier:", error)
      alert("Error saving modifier")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{modifier ? "Edit Modifier List" : "Create New Modifier List"}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Modifier Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g., Size, Spice Level, Add-ons"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Selection Type *</label>
                <select
                  value={formData.selectionType}
                  onChange={(e) => setFormData({ ...formData, selectionType: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="SINGLE">Single Select (Choose one)</option>
                  <option value="MULTIPLE">Multiple Select (Choose multiple)</option>
                </select>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <label className="block text-sm font-medium text-gray-700">Options *</label>
                <button
                  type="button"
                  onClick={addOption}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                >
                  Add Option
                </button>
              </div>

              <div className="space-y-3">
                {formData.options.map((option, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 border rounded-md">
                    <input
                      type="text"
                      placeholder="Option name (e.g., Small, Medium, Large)"
                      value={option.name}
                      onChange={(e) => updateOption(index, "name", e.target.value)}
                      className="flex-1 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-500"
                    />
                    <div className="flex items-center">
                      <span className="text-gray-500 mr-1">+$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={option.price}
                        onChange={(e) => updateOption(index, "price", Number.parseFloat(e.target.value) || 0)}
                        className="w-20 border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-500"
                      />
                    </div>
                    {formData.options.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeOption(index)}
                        className="text-red-600 hover:text-red-800 px-2"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md disabled:opacity-50"
              >
                {loading ? "Saving..." : modifier ? "Update Modifier" : "Create Modifier"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ModifierModal
